<template>
    <transition name="toast">
        <div v-if="show" class="notification-toast" :class="type">
            <div class="toast-accent"></div>
            <div class="toast-icon">
                <CheckCircleIcon v-if="type === 'success'" :size="20" />
                <AlertCircleIcon v-else-if="type === 'error'" :size="20" />
                <AlertTriangleIcon v-else-if="type === 'warning'" :size="20" />
                <InfoIcon v-else :size="20" />
            </div>
            <div class="toast-content">
                <p v-if="title" class="toast-title">{{ title }}</p>
                <p class="toast-message">{{ message }}</p>
            </div>
            <button class="toast-close" @click="$emit('close')" aria-label="Закрыть">
                <XIcon :size="14" />
            </button>
            <div class="toast-progress" :style="{ animationDuration: `${duration}ms` }"></div>
        </div>
    </transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue';
import {
    CheckCircleIcon,
    AlertCircleIcon,
    AlertTriangleIcon,
    InfoIcon,
    XIcon
} from 'lucide-vue-next';

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    message: {
        type: String,
        required: true
    },
    title: {
        type: String,
        default: ''
    },
    type: {
        type: String,
        default: 'info',
        validator: (value: string) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    duration: {
        type: Number,
        default: 4000
    }
});

const emit = defineEmits(['close']);

let timer: ReturnType<typeof setTimeout> | null = null;

const setupTimer = () => {
    clearTimeout(timer!);
    if (props.show) {
        timer = setTimeout(() => emit('close'), props.duration);
    }
};

watch(() => props.show, (val) => {
    if (val) setupTimer();
    else clearTimeout(timer!);
});

onMounted(() => { if (props.show) setupTimer(); });
onUnmounted(() => clearTimeout(timer!));
</script>

<style scoped>
.notification-toast {
    position: fixed;
    bottom: 2rem;
    right: 2rem;
    min-width: 300px;
    max-width: 420px;
    background: #1e1e2e;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 14px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: flex-start;
    padding: 14px 14px 18px 0;
    z-index: 9999;
    overflow: hidden;
    gap: 10px;
}

/* Left accent bar */
.toast-accent {
    width: 4px;
    flex-shrink: 0;
    align-self: stretch;
    border-radius: 0 4px 4px 0;
    margin-right: 2px;
}

.notification-toast.success .toast-accent { background: #4ade80; }
.notification-toast.error   .toast-accent { background: #f87171; }
.notification-toast.warning .toast-accent { background: #fbbf24; }
.notification-toast.info    .toast-accent { background: #60a5fa; }

/* Icon */
.toast-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
}

.notification-toast.success .toast-icon { color: #4ade80; }
.notification-toast.error   .toast-icon { color: #f87171; }
.notification-toast.warning .toast-icon { color: #fbbf24; }
.notification-toast.info    .toast-icon { color: #60a5fa; }

/* Content */
.toast-content {
    flex: 1;
    min-width: 0;
}

.toast-title {
    margin: 0 0 2px;
    font-size: 0.875rem;
    font-weight: 600;
    color: #f0f0f5;
    line-height: 1.3;
}

.toast-message {
    margin: 0;
    font-size: 0.8125rem;
    color: #a0a0b8;
    line-height: 1.5;
    word-break: break-word;
}

/* Close button */
.toast-close {
    flex-shrink: 0;
    background: none;
    border: none;
    color: #555570;
    cursor: pointer;
    padding: 2px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease, background 0.15s ease;
    margin-top: 1px;
    margin-right: 2px;
}

.toast-close:hover {
    color: #c0c0d8;
    background: rgba(255, 255, 255, 0.08);
}

/* Progress bar */
.toast-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: 100%;
    border-radius: 0 0 14px 14px;
    animation: toast-progress-shrink linear forwards;
}

.notification-toast.success .toast-progress { background: #4ade80; }
.notification-toast.error   .toast-progress { background: #f87171; }
.notification-toast.warning .toast-progress { background: #fbbf24; }
.notification-toast.info    .toast-progress { background: #60a5fa; }

@keyframes toast-progress-shrink {
    from { width: 100%; }
    to   { width: 0%; }
}

/* Enter/leave animations */
.toast-enter-active,
.toast-leave-active {
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;
}

.toast-enter-from {
    transform: translateX(110%);
    opacity: 0;
}

.toast-leave-to {
    transform: translateX(110%);
    opacity: 0;
}

@media (max-width: 600px) {
    .notification-toast {
        left: 1rem;
        right: 1rem;
        bottom: 1rem;
        min-width: 0;
        max-width: none;
    }
}
</style>
